import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 60_000;
const RELOADED_VERSION_KEY = "app_reloaded_version";

export type VersionCheckResult = {
  updateAvailable: boolean;
  reload: () => void;
};

export function useVersionCheck(): VersionCheckResult {
  const initialVersionRef = useRef<string | null>(null);
  const latestVersionRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    let checking = false;
    const checkVersion = async () => {
      if (checking) return;
      checking = true;
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: unknown };
        const version = data.version;
        if (typeof version !== "string") return;

        if (initialVersionRef.current === null) {
          initialVersionRef.current = version;
          return;
        }
        if (version !== initialVersionRef.current) {
          const reloadedVersion = sessionStorage.getItem(RELOADED_VERSION_KEY);
          if (reloadedVersion === version) return;
          latestVersionRef.current = version;
          setUpdateAvailable(true);
        }
      } catch {
        // Network/parse error: silently ignore
      } finally {
        checking = false;
      }
    };

    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const loop = async () => {
      await checkVersion();
      if (!cancelled) {
        timeoutId = setTimeout(loop, POLL_INTERVAL_MS);
      }
    };

    void loop();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const reload = useCallback(() => {
    const version = latestVersionRef.current;
    if (version !== null) {
      sessionStorage.setItem(RELOADED_VERSION_KEY, version);
    }
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}
